/**
 * Pixi scene for the PvP roulette wheel.
 * Owns the Application, containers, graphics pool, avatars, and team pattern overlay.
 * WheelEngine is the coordinator; this class handles only rendering.
 */

import {
  Application,
  Assets,
  Container,
  Graphics,
  Matrix,
  Sprite,
  Text,
  TextStyle,
  Texture,
} from "pixi.js"
import type { WheelParty as SectorParty } from "@/entities/pvp-roulette/model/commands"
import type {
  WheelDiagnosticsCallback,
  WheelSectorsLayoutRow,
} from "./wheelDiagnosticsTypes"
import { getPvpRouletteRuntime } from "@/shared/lib/pvp-roulette-runtime/runtime"
import { getAvatarInitials } from "@/shared/lib/avatarInitials"
import {
  resolveAvatarLoadUrl,
  shouldProxyAvatarUrl,
} from "@/shared/lib/avatarProxy"
import {
  TEAM_PATTERN_BACKGROUND_COLOR,
} from "@/shared/constants/pvpRoulette"
import { safeDestroyPixiApplication } from "./pixiTeardown"
import {
  ensureRouletteBackgroundPreloaded,
  ROULETTE_BACKGROUND_TEXTURE_ALIAS,
} from "@/features/pvp-roulette-wheel/assets/rouletteBackground"

// ── Color palette ──────────────────────────────────────────────────────────────
const COLORS = [
  0xf59e0b, 0x3b82f6, 0x8b5cf6, 0x10b981, 0xef4444, 0x06b6d4, 0xec4899,
  0x84cc16, 0xf97316, 0x6366f1,
]

// ── Avatar layer constants ─────────────────────────────────────────────────────
const AVATAR_SIZE = 26
const AVATAR_BORDER_WIDTH = 2
const AVATAR_BORDER_COLOR = 0x000000
const AVATAR_STRIDE = 18
const AVATAR_MID_RADIUS_FACTOR = 0.48
const AVATAR_MAX_SQUAD = 3
const MIN_AVATAR_SECTOR_DEG = 22
const AVATAR_PLACEHOLDER_FILL = 0x4a4d54
const AVATAR_PLACEHOLDER_TEXT = 0xffffff
const YOU_PILL_W = 44
const YOU_PILL_H = 22
const YOU_FONT_SIZE = 11
const AVATAR_TEXTURE_CACHE_MAX_SIZE = 256

// Toggle the legacy white-triangle pointer. Currently the mascot claw plays the
// pointer role — set to true to fall back to the triangle without removing the code.
const SHOW_DEFAULT_POINTER = false

// ── Pure geometry helper ───────────────────────────────────────────────────────

/**
 * Cumulative sector boundary angles [0, pct1*360, pct1*360+pct2*360, ...].
 * Used by WheelEngine for tween normalization and by the scene for drawSectors.
 */
export function computeSectorStartAngles(parties: SectorParty[]): number[] {
  const angles: number[] = [0]
  let acc = 0
  for (let i = 0; i < parties.length; i++) {
    acc += (parties[i].percent / 100) * 360
    angles.push(acc)
  }
  return angles
}

// ─────────────────────────────────────────────────────────────────────────────

export class WheelSceneManager {
  // ── Pixi objects ──────────────────────────────────────────────────────────
  private _app: Application | null = null
  private _containerEl: HTMLElement | null = null
  /** Static plate behind sectors — does not rotate with the wheel. */
  private _plateContainer: Container | null = null
  private _wheelContainer: Container | null = null
  private _slicesContainer: Container | null = null
  private _staticContainer: Container | null = null
  private _avatarContainer: Container | null = null

  // ── Layout ────────────────────────────────────────────────────────────────
  private _size = getPvpRouletteRuntime().WHEEL_DEFAULT_SIZE
  private _outerRadius = 0
  private _innerRadius = 0
  // Extra canvas pixels outside the wheel area (for mascot overflow).
  private _padLeft = 0
  private _padTop = 0

  // ── Graphics pool ─────────────────────────────────────────────────────────
  private sectorGraphicsPool: Graphics[] = []
  private wheelBackgroundSprite: Sprite | null = null
  private wheelBackgroundOuterRadius = 0
  private wheelBackgroundLoadStarted = false
  private userGlowGraphic: Graphics | null = null
  private teamBgGraphic: Graphics | null = null
  private readonly teamPatternTextureTileWidthPx = 256
  private readonly teamPatternTextureTileHeightPx = 64
  private teamPatternOverlayContainer: Container | null = null
  private teamPatternOverlayGraphic: Graphics | null = null
  private teamPatternTexture: Texture | null = null
  private teamPatternUvMatrix: Matrix | null = null
  private teamPatternUvMatrixForTex: Texture | null = null

  // ── Avatar layer ──────────────────────────────────────────────────────────
  private avatarItems: Container[] = []
  private currentUserId: string | null = null
  private readonly textureCache = new Map<string, Texture>()
  private readonly texturePending = new Set<string>()
  private readonly textureFailed = new Set<string>()

  // ── Display state ─────────────────────────────────────────────────────────
  private _currentDisplayAngles: number[] = [0, 360]
  private _lastVisualAngleDeg = 0
  /** Parties currently being rendered (set by drawSectors / drawSectorsFromAngles). */
  private displayParties: SectorParty[] = []

  // ── Diagnostics ───────────────────────────────────────────────────────────
  private initTime = 0
  private diagSectorsNextLayoutAllowedPerf = 0

  constructor(private readonly onDiagnostics?: WheelDiagnosticsCallback) {}

  // ── Getters ───────────────────────────────────────────────────────────────

  get app(): Application | null {
    return this._app
  }
  get wheelContainer(): Container | null {
    return this._wheelContainer
  }
  get slicesContainer(): Container | null {
    return this._slicesContainer
  }
  get staticContainer(): Container | null {
    return this._staticContainer
  }
  get lastVisualAngleDeg(): number {
    return this._lastVisualAngleDeg
  }
  get currentDisplayAngles(): number[] {
    return this._currentDisplayAngles
  }
  get size(): number {
    return this._size
  }
  get outerRadius(): number {
    return this._outerRadius
  }
  get innerRadius(): number {
    return this._innerRadius
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Create a new Pixi Application with the given geometry.
   * Returns the raw app so the engine can do a seq-guard check before calling setupContainers.
   */
  async createApp(
    size: number,
    outerRadius: number,
    innerRadius: number,
    padLeft = 0,
    padTop = 0
  ): Promise<Application> {
    this._size = size
    this._outerRadius = outerRadius
    this._innerRadius = innerRadius
    this._padLeft = padLeft
    this._padTop = padTop
    const app = new Application()
    await app.init({
      width: size + padLeft,
      height: size + padTop,
      backgroundColor: 0x000000,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
    })
    return app
  }

  /** Wire a freshly-created Application into this scene. */
  setupContainers(app: Application, containerEl: HTMLElement): void {
    this._app = app
    this._containerEl = containerEl
    this.initTime = performance.now()

    app.canvas.style.backgroundColor = "transparent"
    app.canvas.style.pointerEvents = "none"
    app.canvas.style.display = "block"
    // Offset the canvas so the wheel area stays visually aligned with the container div,
    // while the extra padding area extends outward for mascot overflow.
    app.canvas.style.position = "absolute"
    app.canvas.style.top = `-${this._padTop}px`
    app.canvas.style.left = `-${this._padLeft}px`

    const plate = new Container()
    const wc = new Container()
    const sc = new Container()
    const stc = new Container()
    const ac = new Container()
    wc.addChild(sc)
    const centerX = this._size / 2 + this._padLeft
    const centerY = this._size / 2 + this._padTop
    plate.x = centerX
    plate.y = centerY
    wc.x = centerX
    wc.y = centerY
    stc.x = centerX
    stc.y = centerY
    ac.x = centerX
    ac.y = centerY
    app.stage.addChild(plate)
    app.stage.addChild(wc)
    app.stage.addChild(ac)
    app.stage.addChild(stc)
    this._plateContainer = plate
    this._wheelContainer = wc
    this._slicesContainer = sc
    this._staticContainer = stc
    this._avatarContainer = ac
    // staticContainer / avatarContainer stay un-rotated — only sector graphics
    // (in wheelContainer) and per-avatar polar positions follow the base rotation.
    void this.syncWheelBackground()
  }

  /** Fast-path resize when the app already exists. Repositions containers but does not rebuild. */
  resize(
    containerEl: HTMLElement,
    size: number,
    outerRadius: number,
    innerRadius: number
  ): void {
    if (
      !this._app ||
      !this._plateContainer ||
      !this._wheelContainer ||
      !this._slicesContainer ||
      !this._staticContainer
    )
      return
    this._size = size
    this._outerRadius = outerRadius
    this._innerRadius = innerRadius
    this._containerEl = containerEl
    this._app.renderer.resize(size + this._padLeft, size + this._padTop)
    this._app.canvas.style.top = `-${this._padTop}px`
    this._app.canvas.style.left = `-${this._padLeft}px`
    const centerX = size / 2 + this._padLeft
    const centerY = size / 2 + this._padTop
    this._plateContainer.x = centerX
    this._plateContainer.y = centerY
    this._wheelContainer.x = centerX
    this._wheelContainer.y = centerY
    this._staticContainer.x = centerX
    this._staticContainer.y = centerY
    if (this._avatarContainer) {
      this._avatarContainer.x = size / 2 + this._padLeft
      this._avatarContainer.y = size / 2 + this._padTop
    }
    this.teamPatternUvMatrix = null
    this.teamPatternUvMatrixForTex = null
    void this.syncWheelBackground()
  }

  /** Reset display state between rounds (keeps Pixi objects alive). */
  resetDisplay(initialAngleDeg = 0): void {
    this._lastVisualAngleDeg = initialAngleDeg
    this._currentDisplayAngles = [0, 360]
    this.diagSectorsNextLayoutAllowedPerf = 0
    if (this._wheelContainer) {
      this._wheelContainer.rotation = -(initialAngleDeg * Math.PI) / 180
    }
  }

  /** Destroy the Pixi application and clear all references. */
  destroy(): void {
    this.clearAvatarLayer()
    const app = this._app
    this._app = null
    safeDestroyPixiApplication(app)
    this.sectorGraphicsPool = []
    this.wheelBackgroundSprite = null
    this.wheelBackgroundOuterRadius = 0
    this.wheelBackgroundLoadStarted = false
    this.userGlowGraphic = null
    this.teamBgGraphic = null
    this.teamPatternOverlayContainer = null
    this.teamPatternOverlayGraphic = null
    this.teamPatternTexture = null
    this.teamPatternUvMatrix = null
    this.teamPatternUvMatrixForTex = null
    this.avatarItems = []
    this._avatarContainer = null
    this.textureCache.clear()
    this.texturePending.clear()
    this._containerEl = null
    this._plateContainer = null
    this._wheelContainer = null
    this._slicesContainer = null
    this._staticContainer = null
  }

  // ── User identity ─────────────────────────────────────────────────────────

  setCurrentUserId(id: string | null, parties: SectorParty[]): void {
    this.currentUserId = id
    if (this._slicesContainer && parties.length > 0) {
      this.drawUserGlow(computeSectorStartAngles(parties), parties)
      this.buildAvatarItems(parties)
    }
  }

  // ── Team pattern ──────────────────────────────────────────────────────────

  async loadTeamPatternTexture(src: string | null): Promise<void> {
    if (!src) {
      this.teamPatternTexture = null
      this.teamPatternUvMatrix = null
      this.teamPatternUvMatrixForTex = null
      return
    }
    const texture = await this.loadRepeatablePatternTexture(src)
    this.teamPatternUvMatrix = null
    this.teamPatternUvMatrixForTex = null
    this.teamPatternTexture = texture
  }

  private async loadRepeatablePatternTexture(src: string): Promise<Texture> {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = src
    })
    const srcW = image.naturalWidth || image.width
    const srcH = image.naturalHeight || image.height

    if (srcW <= 0 || srcH <= 0)
      throw new Error("Team pattern image has invalid dimensions")

    const tileWidth = Math.max(
      1,
      Math.round(getPvpRouletteRuntime().TEAM_PATTERN_TILE_WIDTH)
    )
    const tileHeight = Math.max(
      1,
      Math.round(getPvpRouletteRuntime().TEAM_PATTERN_TILE_HEIGHT)
    )

    const iconWidth = Math.max(
      1,
      Math.min(
        tileWidth,
        Math.round(getPvpRouletteRuntime().TEAM_PATTERN_ICON_WIDTH)
      )
    )
    const iconHeight = Math.max(
      1,
      Math.min(
        tileHeight,
        Math.round(getPvpRouletteRuntime().TEAM_PATTERN_ICON_HEIGHT)
      )
    )
    const potW = this.teamPatternTextureTileWidthPx
    const potH = this.teamPatternTextureTileHeightPx

    const iconPxW = Math.max(1, Math.round((iconWidth / tileWidth) * potW))
    const iconPxH = Math.max(1, Math.round((iconHeight / tileHeight) * potH))

    const iconOffsetX = Math.floor((potW - iconPxW) / 2)
    const iconOffsetY = Math.floor((potH - iconPxH) / 2)

    const canvas = document.createElement("canvas")

    canvas.width = potW
    canvas.height = potH

    const ctx = canvas.getContext("2d")

    if (!ctx)
      throw new Error("Failed to create 2D context for team pattern texture")

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = "high"

    ctx.clearRect(0, 0, potW, potH)

    ctx.globalAlpha = 1

    ctx.drawImage(
      image,
      0,
      0,
      srcW,
      srcH,
      iconOffsetX,
      iconOffsetY,
      iconPxW,
      iconPxH
    )

    ctx.globalAlpha = 1

    const texture = Texture.from(canvas)
    texture.source.wrapMode = "repeat"

    return texture
  }

  // ── Sector drawing ────────────────────────────────────────────────────────

  /** Rebuild sector pool from scratch. Called on scene init and radius change. */
  drawSectors(parties: SectorParty[]): void {
    if (!this._slicesContainer) {
      this.emitSceneDiag("drawSectors_no_slices", {
        partiesArg: parties.length,
      })
      return
    }
    try {
      for (const g of this.sectorGraphicsPool) g.destroy()
      this.sectorGraphicsPool = []
      this.drawTeamBackground(parties)

      if (parties.length === 0) {
        this.drawUserGlow([], [])
        this._currentDisplayAngles = [0, 360]
        this.displayParties = []
        this.buildAvatarItems([])
        this.syncTeamPatternOverlay(this._lastVisualAngleDeg)
        this.emitSectorsLayout("drawSectors", [0, 360], [], 1)
        return
      }

      if (parties.length === 1 && (parties[0].percent ?? 100) >= 99.9) {
        const { innerR, outerR } = this.getSectorRadii()
        const g = this.getPooledGraphics(0)
        this.appendAnnulusSector(g, innerR, outerR, -Math.PI / 2, Math.PI / 2 * 3)
        this.fillSectorGraphics(g, parties[0], 0)
        const fullAngles = computeSectorStartAngles(parties)
        this.drawUserGlow(fullAngles, parties)
        this._currentDisplayAngles = fullAngles
        this.displayParties = parties
        this.buildAvatarItems(parties)
        this.syncTeamPatternOverlay(this._lastVisualAngleDeg)
        this.emitSectorsLayout("drawSectors", fullAngles, parties, 1, {
          fullDiskSingle: true,
        })
        return
      }

      const { innerR, outerR } = this.getSectorRadii()
      let startAngle = -Math.PI / 2
      let drawn = 0
      parties.forEach((p, i) => {
        const sliceAngle = (p.percent / 100) * Math.PI * 2
        if (sliceAngle > 0.01) {
          const g = this.getPooledGraphics(drawn++)
          this.appendAnnulusSector(
            g,
            innerR,
            outerR,
            startAngle,
            startAngle + sliceAngle
          )
          this.fillSectorGraphics(g, p, i)
          startAngle += sliceAngle
        }
      })

      this.hidePoolItemsFrom(drawn)
      const finalAngles = computeSectorStartAngles(parties)
      this.drawUserGlow(finalAngles, parties)
      this._currentDisplayAngles = finalAngles
      this.displayParties = parties
      this.buildAvatarItems(parties)
      this.syncTeamPatternOverlay(this._lastVisualAngleDeg)
      this.emitSectorsLayout("drawSectors", finalAngles, parties, drawn)
    } finally {
      this.emitSceneDiag("drawSectors_done", { partiesArg: parties.length })
    }
  }

  /** Redraw sectors from interpolated angles (per-frame during tween). Reuses pool. */
  drawSectorsFromAngles(
    anglesDeg: number[],
    parties: SectorParty[],
    tweenDiag?: { tweenT: number; forceLayoutLog?: boolean }
  ): void {
    if (!this._slicesContainer) {
      this.emitSceneDiag("drawAngles_no_slices", { partiesArg: parties.length })
      return
    }
    this.displayParties = parties
    this.resetPoolVisibility()
    const segmentCount = anglesDeg.length - 1
    let drawn = 0

    this.drawTeamBackground(parties)

    const { innerR, outerR } = this.getSectorRadii()
    if (
      parties.length === 1 &&
      segmentCount === 1 &&
      Math.abs((anglesDeg[1] ?? 0) - (anglesDeg[0] ?? 0) - 360) < 0.1
    ) {
      const g = this.getPooledGraphics(drawn++)
      this.appendAnnulusSector(g, innerR, outerR, -Math.PI / 2, Math.PI / 2 * 3)
      this.fillSectorGraphics(g, parties[0], 0)
    } else {
      for (let i = 0; i < segmentCount; i++) {
        const startDeg = anglesDeg[i] ?? 0
        const endDeg = anglesDeg[i + 1] ?? 0
        const sa = (startDeg * Math.PI) / 180 - Math.PI / 2
        const ea = (endDeg * Math.PI) / 180 - Math.PI / 2
        if (Math.abs(ea - sa) > 0.01) {
          const g = this.getPooledGraphics(drawn++)
          this.appendAnnulusSector(g, innerR, outerR, sa, ea)
          this.fillSectorGraphics(g, parties[i], i)
        }
      }
    }

    if (parties.length > 0 && drawn === 0) {
      this.emitSceneDiag("drawAngles_zero_arcs", { partiesArg: parties.length })
    }

    this.hidePoolItemsFrom(drawn)
    this.drawUserGlow(anglesDeg, parties)
    this._currentDisplayAngles = anglesDeg
    this.emitSectorsLayout("drawSectorsFromAngles", anglesDeg, parties, drawn, {
      tweenT: tweenDiag?.tweenT,
      force: tweenDiag?.forceLayoutLog === true,
    })
  }

  // ── Static scene elements ─────────────────────────────────────────────────

  /** PNG wheel plate (replaces coded center disk + outer stroke). */
  drawCenter(): void {
    void this.syncWheelBackground()
  }

  private getSectorRadii(): { innerR: number; outerR: number } {
    const innerR = this._innerRadius
    const inset = getPvpRouletteRuntime().WHEEL_SECTOR_OUTER_INSET_PX
    const outerR = Math.max(innerR + 1, this._outerRadius - inset)
    return { innerR, outerR }
  }

  private appendAnnulusSector(
    g: Graphics,
    innerR: number,
    outerR: number,
    startRad: number,
    endRad: number
  ): void {
    if (outerR <= innerR || Math.abs(endRad - startRad) <= 0.0001) return
    g.moveTo(Math.cos(startRad) * outerR, Math.sin(startRad) * outerR)
    g.arc(0, 0, outerR, startRad, endRad)
    g.lineTo(Math.cos(endRad) * innerR, Math.sin(endRad) * innerR)
    g.arc(0, 0, innerR, endRad, startRad, true)
    g.closePath()
  }

  private async syncWheelBackground(): Promise<void> {
    if (!this._plateContainer || this._outerRadius <= 0) return

    if (!this.wheelBackgroundLoadStarted) {
      this.wheelBackgroundLoadStarted = true
      try {
        await ensureRouletteBackgroundPreloaded()
        const texture = Assets.get(ROULETTE_BACKGROUND_TEXTURE_ALIAS)
        if (!texture || !this._plateContainer) return
        if (!this.wheelBackgroundSprite) {
          this.wheelBackgroundSprite = new Sprite(texture)
          this.wheelBackgroundSprite.anchor.set(0.5)
        } else {
          this.wheelBackgroundSprite.texture = texture
        }
        this.layoutWheelBackgroundSprite()
        this.ensureWheelBackgroundBehindSlices()
        this._app?.render()
      } catch (err) {
        console.error("[WheelSceneManager] roulette background failed:", err)
      }
      return
    }

    if (this.wheelBackgroundSprite) {
      this.layoutWheelBackgroundSprite()
      this.ensureWheelBackgroundBehindSlices()
    }
  }

  private layoutWheelBackgroundSprite(): void {
    const sprite = this.wheelBackgroundSprite
    if (!sprite) return
    const diameter = this._outerRadius * 2
    sprite.width = diameter
    sprite.height = diameter
    sprite.x = 0
    sprite.y = 0
    if (this.wheelBackgroundOuterRadius !== this._outerRadius) {
      this.wheelBackgroundOuterRadius = this._outerRadius
    }
  }

  private ensureWheelBackgroundBehindSlices(): void {
    const sprite = this.wheelBackgroundSprite
    if (!sprite || !this._plateContainer) return
    if (sprite.parent !== this._plateContainer) {
      this._plateContainer.addChild(sprite)
    }
  }

  drawPointer(): void {
    if (!this._staticContainer) return
    const prevChildren = this._staticContainer.removeChildren()
    prevChildren.forEach((c) => c.destroy({ children: true }))
    if (!SHOW_DEFAULT_POINTER) return

    const scale = 1.4
    const r = this._size * 0.46
    const topY = -r - 12
    const g = new Graphics()
    const cx = 5.48438 * scale
    const s = scale

    g.moveTo(-cx + 5.48438 * s, topY + 0.438477 * s)
    g.bezierCurveTo(
      -cx + 6.21515 * s,
      topY + 0.438477 * s,
      -cx + 6.94551 * s,
      topY + 0.445765 * s,
      -cx + 7.6748 * s,
      topY + 0.461914 * s
    )
    g.bezierCurveTo(
      -cx + 9.27325 * s,
      topY + 0.497313 * s,
      -cx + 10.5301 * s,
      topY + 1.82803 * s,
      -cx + 10.5303 * s,
      topY + 3.4502 * s
    )
    g.lineTo(-cx + 10.5303 * s, topY + 15.9727 * s)
    g.lineTo(-cx + 5.48438 * s, topY + 20.5029 * s)
    g.lineTo(-cx + 0.438477 * s, topY + 15.9727 * s)
    g.lineTo(-cx + 0.438477 * s, topY + 3.47754 * s)
    g.bezierCurveTo(
      -cx + 0.438477 * s,
      topY + 1.8461 * s,
      -cx + 1.70943 * s,
      topY + 0.505404 * s,
      -cx + 3.32129 * s,
      topY + 0.46582 * s
    )
    g.bezierCurveTo(
      -cx + 4.0419 * s,
      topY + 0.448198 * s,
      -cx + 4.76345 * s,
      topY + 0.438477 * s,
      -cx + 5.48438 * s,
      topY + 0.438477 * s
    )
    g.closePath()
    g.fill(0xffffff)
    g.stroke({ width: 0.877192 * s, color: 0x1b1c21 })
    this._staticContainer.addChild(g)
  }

  // ── Per-frame render ──────────────────────────────────────────────────────

  /** Apply wheel rotation + avatar repositioning + team pattern overlay. */
  render(visualAngleDeg: number): void {
    this._lastVisualAngleDeg = visualAngleDeg
    if (this._wheelContainer) {
      this._wheelContainer.rotation = -(visualAngleDeg * Math.PI) / 180
    }
    this.updateAvatarPositions(visualAngleDeg)
    this.syncTeamPatternOverlay(visualAngleDeg)
  }

  // ── Avatar layer ──────────────────────────────────────────────────────────

  private clearAvatarLayer(): void {
    for (const item of this.avatarItems) {
      try {
        item.destroy({ children: true })
      } catch {
        // Pixi Text pool can throw if the renderer is already gone.
      }
    }
    this.avatarItems = []
    try {
      this._avatarContainer?.removeChildren()
    } catch {
      // Container may already be destroyed with the Application.
    }
  }

  buildAvatarItems(parties: SectorParty[]): void {
    if (!this._avatarContainer) return
    this.prefetchAvatarTextures(parties)
    this.clearAvatarLayer()

    for (const party of parties) {
      const partyContainer = new Container()
      type Slot = {
        userId: string
        username?: string | null
        avatarUrl?: string | null
        isMe: boolean
      }
      let slots: Slot[]
      const isTeam = party.partyType === "TEAM"
      if (isTeam && party.members && party.members.length > 0) {
        const meIdx = party.members.findIndex(
          (m) => m.userId === this.currentUserId
        )
        const ordered =
          meIdx >= 0
            ? [
                party.members[meIdx]!,
                ...party.members.filter((_, i) => i !== meIdx),
              ]
            : [...party.members]
        slots = ordered.map((m) => ({
          userId: m.userId,
          username: m.username,
          avatarUrl: m.avatarUrl,
          isMe: m.userId === this.currentUserId,
        }))
      } else {
        slots = [
          {
            userId: party.userId,
            username: party.username,
            avatarUrl: party.avatarUrl,
            isMe: party.userId === this.currentUserId,
          },
        ]
      }

      const displayed = slots.slice(0, AVATAR_MAX_SQUAD)
      const overflow = slots.length - displayed.length
      const totalItems = displayed.length + (overflow > 0 ? 1 : 0)
      const groupWidth = AVATAR_SIZE + (totalItems - 1) * AVATAR_STRIDE
      const startX = -groupWidth / 2 + AVATAR_SIZE / 2

      for (let i = 0; i < displayed.length; i++) {
        const slot = displayed[i]!
        const slotEl = slot.isMe
          ? this.createYouPill()
          : this.createAvatarCircle(
              slot.avatarUrl,
              slot.username ?? slot.userId,
              AVATAR_SIZE
            )
        slotEl.x = startX + i * AVATAR_STRIDE
        partyContainer.addChild(slotEl)
      }
      if (overflow > 0) {
        const badge = this.createOverflowBadge(overflow)
        badge.x = startX + displayed.length * AVATAR_STRIDE
        partyContainer.addChild(badge)
      }

      this._avatarContainer.addChild(partyContainer)
      this.avatarItems.push(partyContainer)
    }
    this.updateAvatarPositions(this._lastVisualAngleDeg)
  }

  private updateAvatarPositions(visualAngleDeg: number): void {
    if (!this._avatarContainer) return
    const angles = this._currentDisplayAngles
    const midR =
      this._innerRadius +
      (this._outerRadius - this._innerRadius) * AVATAR_MID_RADIUS_FACTOR

    for (let i = 0; i < this.avatarItems.length; i++) {
      const item = this.avatarItems[i]
      if (!item) continue
      const startDeg = angles[i] ?? 0
      const endDeg = angles[i + 1] ?? 0
      const spanDeg = endDeg - startDeg
      if (spanDeg < MIN_AVATAR_SECTOR_DEG) {
        item.visible = false
        continue
      }
      item.visible = true
      const midDeg = (startDeg + endDeg) / 2
      const worldRad = ((midDeg - visualAngleDeg) * Math.PI) / 180 - Math.PI / 2
      item.x = Math.cos(worldRad) * midR
      item.y = Math.sin(worldRad) * midR
    }
  }

  private createYouPill(): Container {
    const c = new Container()
    const pill = new Graphics()
    pill.roundRect(
      -YOU_PILL_W / 2,
      -YOU_PILL_H / 2,
      YOU_PILL_W,
      YOU_PILL_H,
      YOU_PILL_H / 2
    )
    pill.fill(0xffffff)
    pill.stroke({ width: 1.5, color: 0x1b1c21 })
    const label = new Text({
      text: "YOU",
      style: new TextStyle({
        fontFamily: "Arial, sans-serif",
        fontSize: YOU_FONT_SIZE,
        fontWeight: "bold",
        fill: 0x000000,
      }),
    })
    label.anchor.set(0.5, 0.5)
    c.addChild(pill, label)
    return c
  }

  private createAvatarCircle(
    avatarUrl: string | null | undefined,
    fallbackName: string,
    size: number
  ): Container {
    const c = new Container()
    const r = size / 2
    const bg = new Graphics()
    bg.circle(0, 0, r)
    const texture = avatarUrl ? this.getCachedTexture(avatarUrl) : null
    bg.fill(texture ? 0x2a2d33 : AVATAR_PLACEHOLDER_FILL)
    c.addChild(bg)
    if (texture) {
      const mask = new Graphics()
      mask.circle(0, 0, r)
      mask.fill(0xffffff)
      c.addChild(mask)
      const sprite = new Sprite(texture)
      sprite.width = size
      sprite.height = size
      sprite.anchor.set(0.5, 0.5)
      sprite.mask = mask
      c.addChild(sprite)
    } else if (avatarUrl) {
      this.loadAndCacheTexture(avatarUrl)
    }
    if (!texture) {
      const initials = getAvatarInitials(fallbackName, "?", 1)
      const label = new Text({
        text: initials,
        style: new TextStyle({
          fontFamily: "Arial, sans-serif",
          fontSize: Math.max(10, size * 0.42),
          fontWeight: "bold",
          fill: AVATAR_PLACEHOLDER_TEXT,
        }),
      })
      label.anchor.set(0.5, 0.5)
      c.addChild(label)
    }
    const border = new Graphics()
    border.circle(0, 0, r + AVATAR_BORDER_WIDTH / 2)
    border.stroke({ width: AVATAR_BORDER_WIDTH, color: AVATAR_BORDER_COLOR })
    c.addChild(border)
    return c
  }

  private createOverflowBadge(count: number): Container {
    const c = new Container()
    const r = AVATAR_SIZE / 2
    const bg = new Graphics()
    bg.circle(0, 0, r)
    bg.fill(0x2c2f36)
    const label = new Text({
      text: `+${count}`,
      style: new TextStyle({
        fontFamily: "Arial, sans-serif",
        fontSize: AVATAR_SIZE * 0.38,
        fontWeight: "bold",
        fill: 0xffffff,
      }),
    })
    label.anchor.set(0.5, 0.5)
    const border = new Graphics()
    border.circle(0, 0, r + AVATAR_BORDER_WIDTH / 2)
    border.stroke({ width: AVATAR_BORDER_WIDTH, color: AVATAR_BORDER_COLOR })
    c.addChild(bg, label, border)
    return c
  }

  private loadAndCacheTexture(url: string): void {
    if (
      this.textureCache.has(url) ||
      this.texturePending.has(url) ||
      this.textureFailed.has(url)
    )
      return
    this.texturePending.add(url)
    const finishOk = (texture: Texture) => {
      this.texturePending.delete(url)
      this.setCachedTexture(url, texture)
      this.buildAvatarItems(this.displayParties)
    }
    const finishErr = () => {
      this.texturePending.delete(url)
      this.textureFailed.add(url)
    }
    const isProxyTarget = shouldProxyAvatarUrl(url)
    const loadUrl = resolveAvatarLoadUrl(url)
    if (!/^https?:\/\//i.test(url)) {
      Assets.load(url)
        .then((texture: Texture) => finishOk(texture))
        .catch(finishErr)
      return
    }
    const loadViaBlob = (): Promise<Texture> =>
      fetch(loadUrl, {
        mode: "no-cors",
        credentials: "omit",
        referrerPolicy: "no-referrer",
      }).then(async (res) => {
        const blob = await res.blob()
        if (blob.size === 0) throw new Error("empty avatar response")
        const objectUrl = URL.createObjectURL(blob)
        try {
          const img = new Image()
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve()
            img.onerror = () => reject(new Error("blob img"))
            img.src = objectUrl
          })
          await img.decode()
          return Texture.from(img)
        } finally {
          URL.revokeObjectURL(objectUrl)
        }
      })
    const loadViaImageElement = (cors: boolean): Promise<Texture> => {
      const img = new Image()
      img.referrerPolicy = "no-referrer"
      if (cors) img.crossOrigin = "anonymous"
      return new Promise((resolve, reject) => {
        img.onload = () => {
          const apply = () => {
            try {
              resolve(Texture.from(img))
            } catch (e) {
              reject(e)
            }
          }
          if (typeof img.decode === "function") {
            void img
              .decode()
              .then(apply)
              .catch(() => apply())
          } else {
            apply()
          }
        }
        img.onerror = () => reject(new Error("img error"))
        img.src = loadUrl
      })
    }
    let sameOrigin = false
    if (typeof window !== "undefined") {
      try {
        sameOrigin =
          new URL(url, window.location.href).origin === window.location.origin
      } catch {
        sameOrigin = false
      }
    }
    void (async () => {
      if (isProxyTarget) {
        try {
          finishOk(await loadViaImageElement(true))
          return
        } catch {
          // Proxy response may occasionally miss ACAO for specific origins.
          // Fall through to the generic fallback chain instead of hard-failing.
        }
      }
      if (sameOrigin) {
        try {
          finishOk(await loadViaImageElement(false))
          return
        } catch {
          /* */
        }
      }
      try {
        finishOk(await loadViaImageElement(true))
        return
      } catch {
        /* CDN may omit ACAO */
      }
      try {
        finishOk(await loadViaBlob())
        return
      } catch {
        /* */
      }
      finishErr()
    })()
  }

  private getCachedTexture(url: string): Texture | null {
    const texture = this.textureCache.get(url) ?? null
    if (!texture) return null
    this.textureCache.delete(url)
    this.textureCache.set(url, texture)
    return texture
  }

  private setCachedTexture(url: string, texture: Texture): void {
    if (this.textureCache.has(url)) this.textureCache.delete(url)
    this.textureCache.set(url, texture)
    while (this.textureCache.size > AVATAR_TEXTURE_CACHE_MAX_SIZE) {
      const oldestKey = this.textureCache.keys().next().value as
        | string
        | undefined
      if (!oldestKey) break
      this.textureCache.delete(oldestKey)
    }
  }

  private prefetchAvatarTextures(parties: SectorParty[]): void {
    for (const party of parties) {
      if (
        party.partyType === "TEAM" &&
        party.members &&
        party.members.length > 0
      ) {
        for (const member of party.members) {
          if (member.avatarUrl) this.loadAndCacheTexture(member.avatarUrl)
        }
      } else if (party.avatarUrl) {
        this.loadAndCacheTexture(party.avatarUrl)
      }
    }
  }

  // ── Private rendering helpers ─────────────────────────────────────────────

  private getPooledGraphics(index: number): Graphics {
    while (this.sectorGraphicsPool.length <= index) {
      const g = new Graphics()
      this.sectorGraphicsPool.push(g)
      this._slicesContainer!.addChild(g)
    }
    const g = this.sectorGraphicsPool[index]!
    g.clear()
    return g
  }

  private hidePoolItemsFrom(count: number): void {
    for (let i = count; i < this.sectorGraphicsPool.length; i++) {
      this.sectorGraphicsPool[i]!.clear()
      this.sectorGraphicsPool[i]!.visible = false
    }
  }

  private resetPoolVisibility(): void {
    for (const g of this.sectorGraphicsPool) g.visible = true
  }

  private drawUserGlow(anglesDeg: number[], parties: SectorParty[]): void {
    if (!this._slicesContainer) return
    if (!this.userGlowGraphic) this.userGlowGraphic = new Graphics()
    this._slicesContainer.addChild(this.userGlowGraphic)
    this.userGlowGraphic.clear()
    if (!this.currentUserId) return
    const userIdx = parties.findIndex(
      (p) => p.userId === this.currentUserId && p.partyType !== "TEAM"
    )
    if (userIdx === -1) return
    const startDeg = anglesDeg[userIdx] ?? 0
    const endDeg = anglesDeg[userIdx + 1] ?? 0
    if (Math.abs(endDeg - startDeg) < 0.5) return
    const startRad = (startDeg * Math.PI) / 180 - Math.PI / 2
    const endRad = (endDeg * Math.PI) / 180 - Math.PI / 2
    const { outerR } = this.getSectorRadii()
    const color = 0x0c9e4f
    if (parties.length > 1) {
      const EDGE_LAYERS = 20
      const ANGULAR_DEPTH = 0.109
      const lineFrom = this._innerRadius
      const lineTo = outerR - 2
      for (const [boundaryRad, sign] of [
        [startRad, -1],
        [endRad, 1],
      ] as [number, number][]) {
        for (let i = 0; i < EDGE_LAYERS; i++) {
          const frac = i / (EDGE_LAYERS - 1)
          const glowRad = boundaryRad + sign * frac * ANGULAR_DEPTH
          const alpha = 0.65 * Math.exp(-3.5 * frac)
          const width = 1 + frac
          const x1 = Math.cos(glowRad) * lineFrom
          const y1 = Math.sin(glowRad) * lineFrom
          const x2 = Math.cos(glowRad) * lineTo
          const y2 = Math.sin(glowRad) * lineTo
          this.userGlowGraphic.moveTo(x1, y1)
          this.userGlowGraphic.lineTo(x2, y2)
          this.userGlowGraphic.stroke({ width, color, alpha })
        }
      }
    }
  }

  private fillSectorGraphics(
    g: Graphics,
    party: SectorParty | undefined,
    fallbackIdx: number
  ): void {
    if (party?.partyType === "TEAM" && this.teamPatternTexture) {
      g.fill(TEAM_PATTERN_BACKGROUND_COLOR)
    } else {
      const c = party?.color
      const color = c != null ? c : COLORS[fallbackIdx % COLORS.length]
      g.fill(color)
    }
  }

  private drawTeamBackground(parties: SectorParty[]): void {
    if (!this._slicesContainer) return
    const hasTeam =
      this.teamPatternTexture != null &&
      parties.some((p) => p.partyType === "TEAM")
    if (!this.teamBgGraphic) this.teamBgGraphic = new Graphics()
    if (this.teamBgGraphic.parent !== this._slicesContainer) {
      this._slicesContainer.addChildAt(this.teamBgGraphic, 0)
    } else if (this._slicesContainer.getChildIndex(this.teamBgGraphic) !== 0) {
      this._slicesContainer.setChildIndex(this.teamBgGraphic, 0)
    }
    this.teamBgGraphic.clear()
    if (hasTeam) {
      const { innerR, outerR } = this.getSectorRadii()
      this.appendAnnulusSector(
        this.teamBgGraphic,
        innerR,
        outerR,
        -Math.PI / 2,
        Math.PI / 2 * 3
      )
      this.teamBgGraphic.fill(TEAM_PATTERN_BACKGROUND_COLOR)
    }
  }

  private buildTeamPatternGlobalMatrix(tex: Texture): Matrix {
    if (this.teamPatternUvMatrix && this.teamPatternUvMatrixForTex === tex)
      return this.teamPatternUvMatrix
    const rotationRad =
      (getPvpRouletteRuntime().TEAM_PATTERN_ROTATION_DEG +
        getPvpRouletteRuntime().TEAM_PATTERN_GLOBAL_UV_ROTATION_OFFSET_DEG) *
      (Math.PI / 180)
    const cos = Math.cos(rotationRad)
    const sin = Math.sin(rotationRad)

    const srcW = Math.max(1, tex.source.width)
    const srcH = Math.max(1, tex.source.height)

    const logicalTileW = Math.max(
      1,
      getPvpRouletteRuntime().TEAM_PATTERN_TILE_WIDTH
    )
    const logicalTileH = Math.max(
      1,
      getPvpRouletteRuntime().TEAM_PATTERN_TILE_HEIGHT
    )

    const sx = logicalTileW / srcW
    const sy = logicalTileH / srcH

    const phaseX = srcW * 0.5
    const phaseY = srcH * 0.5

    const m = new Matrix(
      cos * sx,
      -sin * sy,
      sin * sx,
      cos * sy,
      phaseX,
      phaseY
    )
    this.teamPatternUvMatrix = m
    this.teamPatternUvMatrixForTex = tex
    return m
  }

  private ensureTeamPatternOverlay(): void {
    if (this.teamPatternOverlayContainer && this.teamPatternOverlayGraphic)
      return
    const c = new Container()
    const g = new Graphics()
    c.addChild(g)
    this.teamPatternOverlayContainer = c
    this.teamPatternOverlayGraphic = g
  }

  private syncTeamPatternOverlay(visualAngleDeg: number): void {
    if (!this._slicesContainer || !this.teamPatternTexture) {
      if (this.teamPatternOverlayContainer)
        this.teamPatternOverlayContainer.visible = false
      return
    }
    const parties = this.displayParties
    if (!parties.some((p) => p.partyType === "TEAM")) {
      if (this.teamPatternOverlayContainer)
        this.teamPatternOverlayContainer.visible = false
      return
    }
    this.ensureTeamPatternOverlay()
    const c = this.teamPatternOverlayContainer!
    const g = this.teamPatternOverlayGraphic!
    c.visible = true
    c.rotation = (visualAngleDeg * Math.PI) / 180
    const tex = this.teamPatternTexture
    const m = this.buildTeamPatternGlobalMatrix(tex)
    g.clear()
    const anglesDeg = this._currentDisplayAngles
    const segmentCount = anglesDeg.length - 1
    const patternFill = {
      texture: tex,
      matrix: m,
      textureSpace: "global" as const,
    }
    const { innerR, outerR } = this.getSectorRadii()
    if (
      segmentCount === 1 &&
      Math.abs((anglesDeg[1] ?? 0) - (anglesDeg[0] ?? 0) - 360) < 0.1 &&
      parties[0]?.partyType === "TEAM"
    ) {
      this.appendAnnulusSector(g, innerR, outerR, -Math.PI / 2, Math.PI / 2 * 3)
      g.fill(patternFill)
    } else {
      for (let i = 0; i < segmentCount; i++) {
        if (parties[i]?.partyType !== "TEAM") continue
        const startDeg = anglesDeg[i] ?? 0
        const endDeg = anglesDeg[i + 1] ?? 0
        const adjStart = startDeg - visualAngleDeg
        const adjEnd = endDeg - visualAngleDeg
        const sa = (adjStart * Math.PI) / 180 - Math.PI / 2
        const ea = (adjEnd * Math.PI) / 180 - Math.PI / 2
        if (Math.abs(ea - sa) <= 0.01) continue
        this.appendAnnulusSector(g, innerR, outerR, sa, ea)
        g.fill(patternFill)
      }
    }
    this._slicesContainer.addChild(c)
    if (this.userGlowGraphic)
      this._slicesContainer.addChild(this.userGlowGraphic)
  }

  // ── Diagnostics ───────────────────────────────────────────────────────────

  private emitSectorsLayout(
    source: "drawSectors" | "drawSectorsFromAngles",
    anglesDeg: number[],
    parties: SectorParty[],
    poolDrawn: number,
    opts?: { tweenT?: number; force?: boolean; fullDiskSingle?: boolean }
  ): void {
    if (!this.onDiagnostics) return
    const wallNow = performance.now()
    if (source === "drawSectorsFromAngles" && !opts?.force) {
      if (wallNow < this.diagSectorsNextLayoutAllowedPerf) return
      this.diagSectorsNextLayoutAllowedPerf = wallNow + 120
    }
    const ts = wallNow - this.initTime
    const segmentCount = Math.max(0, anglesDeg.length - 1)
    const fullDiskSingle = !!(
      opts?.fullDiskSingle ??
      (parties.length === 1 &&
        segmentCount === 1 &&
        Math.abs((anglesDeg[1] ?? 0) - (anglesDeg[0] ?? 0) - 360) < 0.1)
    )
    const sectors: WheelSectorsLayoutRow[] = []
    if (parties.length > 0 && fullDiskSingle && parties[0]) {
      const p = parties[0]
      sectors.push({
        i: 0,
        userId: p.userId,
        sectorId: p.sectorId,
        partyType: p.partyType,
        percent:
          typeof p.percent === "number" ? p.percent : Number(p.percent) || 0,
        spanDeg: 360,
        startDeg: anglesDeg[0] ?? 0,
        endDeg: anglesDeg[1] ?? 360,
      })
    } else if (!fullDiskSingle) {
      for (let i = 0; i < Math.min(segmentCount, parties.length); i++) {
        const p = parties[i]!
        const startDeg = anglesDeg[i] ?? 0
        const endDeg = anglesDeg[i + 1] ?? 0
        sectors.push({
          i,
          userId: p.userId,
          sectorId: p.sectorId,
          partyType: p.partyType,
          percent:
            typeof p.percent === "number" ? p.percent : Number(p.percent) || 0,
          spanDeg: endDeg - startDeg,
          startDeg,
          endDeg,
        })
      }
    }
    const anglePartyMismatch =
      segmentCount > 0 &&
      parties.length > 0 &&
      parties.length !== segmentCount &&
      !fullDiskSingle
    this.onDiagnostics({
      type: "sectors_layout",
      ts,
      source,
      partyCount: parties.length,
      segmentCount,
      poolDrawn,
      anglesDeg: anglesDeg.map((a) => Number(a.toFixed(3))),
      sectors,
      fullDiskSingle,
      anglePartyMismatch,
      ...(opts?.tweenT !== undefined ? { tweenT: opts.tweenT } : {}),
    })
  }

  private emitSceneDiag(tag: string, opts?: { partiesArg?: number }): void {
    if (!this.onDiagnostics) return
    const canvas = this._app?.canvas
    const cont = this._containerEl
    let canvasCssW = 0
    let canvasCssH = 0
    if (canvas) {
      const r = canvas.getBoundingClientRect()
      canvasCssW = r.width
      canvasCssH = r.height
    }
    this.onDiagnostics({
      type: "wheel_scene",
      ts: performance.now() - this.initTime,
      tag,
      canvasInDom: !!canvas?.isConnected,
      canvasCssW,
      canvasCssH,
      containerW: cont?.clientWidth ?? 0,
      containerH: cont?.clientHeight ?? 0,
      sizeCfg: this._size,
      outerR: this._outerRadius,
      innerR: this._innerRadius,
      hasWheel: this._wheelContainer != null,
      hasSlices: this._slicesContainer != null,
      slicesChildren: this._slicesContainer?.children.length ?? 0,
      poolSize: this.sectorGraphicsPool.length,
      partiesArg: opts?.partiesArg ?? -1,
      thisPartiesLen: this.displayParties.length,
      wheelVisible: this._wheelContainer?.visible ?? false,
      slicesVisible: this._slicesContainer?.visible ?? false,
      wheelAlpha: this._wheelContainer?.alpha ?? 0,
      slicesAlpha: this._slicesContainer?.alpha ?? 0,
    })
  }
}
